import Axios from '../../utils/axios';
import { getBackendErrorMessage } from '../../utils/errorHandler';

export interface Provider {
  id: number;
  ID?: number;
  code?: string;
  name: string;
  legal_name?: string;
  revenue_stream?: string;
  business_type?: string;
  tax_identification_number?: string;
  identification_number?: string;
  email?: string;
  phone?: string;
  phone_number?: string;
  contact_person?: string;
  contact_phone_number?: string;
  country_code?: string;
  address?: string;
  province?: string;
  city?: string;
  district?: string;
  sub_district?: string;
  zip_code?: string;
  client_key?: string;
  client_secret?: string;
  verified_at?: string | null;
  self_order_enabled?: boolean;
  area?: string;
  monthly_deliveries?: string;
  existing_partners?: string;
  onboard_category?: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
}

export interface ProviderListResponse {
  data: Provider[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export const getListProvider = async (params: {
  page?: string | number;
  size?: string | number;
  search?: string;
  revenue_stream?: string;
  onboard_category?: string;
  verified?: boolean;
  name_empty?: boolean;
  sort?: string;
  order?: string;
}): Promise<ProviderListResponse> => {
  try {
    const response = await Axios.get('/v1/providers', { params });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getProviderById = async (id: string | number): Promise<Provider> => {
  try {
    const response = await Axios.get(`/v1/providers/${id}`);
    return response.data?.data ?? response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const createProvider = async (data: any): Promise<Provider> => {
  try {
    const response = await Axios.post('/v1/providers', data);
    return response.data?.data ?? response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const updateProvider = async (
  id: string | number,
  data: any
): Promise<Provider> => {
  try {
    const response = await Axios.patch(`/v1/providers/${id}`, data);
    return response.data?.data ?? response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const deleteProvider = async (id: string | number): Promise<void> => {
  try {
    await Axios.delete(`/v1/providers/${id}`);
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const exportProviders = async (params: any = {}): Promise<Blob> => {
  try {
    const response = await Axios.get('/v1/providers/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** Option shape consumed by the searchable client `<SearchSelect>` dropdowns. */
export interface ProviderOption {
  id: string;
  name: string;
  /** The provider's short code (`Provider.code`), when it has one. */
  code: string | null;
}

export const searchProviderOptions = async (
  search?: string
): Promise<ProviderOption[]> => {
  const res = await getListProvider({
    page: '1',
    size: '200',
    search: search?.trim() || undefined,
  });
  return (res.data ?? []).map((p) => ({
    id: String(p.id),
    name: p.name,
    code: p.code?.trim() || null,
  }));
};

export const getProviderNamesByIds = async (
  ids: number[]
): Promise<Record<number, string>> => {
  const wanted = new Set(ids.filter((id) => Number.isFinite(id)));
  const out: Record<number, string> = {};
  if (wanted.size === 0) return out;
  const SIZE = 200;
  const MAX_PAGES = 25;
  for (let page = 1; page <= MAX_PAGES && wanted.size > 0; page++) {
    const res = await getListProvider({ page: String(page), size: String(SIZE) });
    const data = res.data ?? [];
    for (const p of data) {
      if (wanted.has(p.id)) {
        out[p.id] = p.name;
        wanted.delete(p.id);
      }
    }
    const lastPage = res.pagination?.lastPage ?? page;
    if (data.length === 0 || page >= lastPage) break;
  }
  return out;
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider Config
// ─────────────────────────────────────────────────────────────────────────────

export interface ConfigItem {
  value: any;
  type?: 'BOOLEAN' | 'NUMBER' | 'STRING' | 'STRING_ARRAY';
  description?: string;
}

export interface ProviderConfigMap {
  [key: string]: ConfigItem;
}

export const CONFIG_LABELS: Record<string, string> = {
  CLIENT_SERVICE: 'Client Service',
  ALLOW_SELF_ORDER_CUSTOMER: 'Allow Self-Order Customer',
  ENABLE_RETURN_DESTINATION: 'Enable Return Destination',
  ENABLE_AUTO_REALLOCATION: 'Enable Auto-Reallocation',
  REALLOCATION_INTERVAL_MINUTES: 'Reallocation Interval',
  MASKED_PACKAGE_NAME: 'Masked Package Name',
  PORTAL_ALLOW_INVOICING_SUMMARY: 'Allow Invoicing Summary',
  PORTAL_BYPASS_SELF_SERVICE_CLIENT: 'Bypass Self-Service Client',
  PORTAL_ALLOW_SCHEDULE: 'Allow Schedule',
  PORTAL_MULTI_STOP_OPTIONS: 'Multi-Stop Options',
  PORTAL_COD_MODE: 'COD Mode',
  PORTAL_CASH_ADVANCE_ENABLED_OUTLETS: 'Cash Advance Outlets',
  MIN_POP: 'Min Proof of Pickup (POP)',
  MAX_POP: 'Max Proof of Pickup (POP)',
  MIN_POD: 'Min Proof of Delivery (POD)',
  MAX_POD: 'Max Proof of Delivery (POD)',
  POD_PER_PACKAGE: 'Proof of Delivery per Package',
  ENABLE_RETUR_H0: 'Enable Retur H0',
  SHIPPING_DOC_SIGNATURE_OPTIONS: 'Shipping Doc Signature Options',
  INCLUDE_ACTUAL_RECIPIENT_RELATION: 'Include Actual Recipient Relation',
  ALLOCATION_RADIUS_INSTANT: 'Allocation Radius — Instant',
  ALLOCATION_RADIUS_SAME_DAY: 'Allocation Radius — Same Day',
  ALLOCATION_RADIUS_FALLBACK: 'Allocation Radius — Fallback',
  ALLOCATION_RADIUS_MULTI_STOP: 'Allocation Radius — Multi-Stop',
  DELIVERY_STATUS_UPDATE_GEOFENCE_RADIUS: 'Delivery Geofence Radius',
  MAX_GROUP_DELIVERY_SIZE_INSTANT: 'Max Group Size — Instant',
  MAX_GROUP_DELIVERY_SIZE_SAME_DAY: 'Max Group Size — Same Day',
  MAX_GROUP_DELIVERY_SIZE_FALLBACK: 'Max Group Size — Fallback',
};

export const CONFIG_UNITS: Record<string, string> = {
  REALLOCATION_INTERVAL_MINUTES: 'min',
  ALLOCATION_RADIUS_INSTANT: 'm',
  ALLOCATION_RADIUS_SAME_DAY: 'm',
  ALLOCATION_RADIUS_FALLBACK: 'm',
  ALLOCATION_RADIUS_MULTI_STOP: 'm',
  DELIVERY_STATUS_UPDATE_GEOFENCE_RADIUS: 'm',
  MAX_GROUP_DELIVERY_SIZE_INSTANT: 'deliveries',
  MAX_GROUP_DELIVERY_SIZE_SAME_DAY: 'deliveries',
  MAX_GROUP_DELIVERY_SIZE_FALLBACK: 'deliveries',
};

export const PROVIDER_CONFIG_CATEGORIES = [
  {
    label: 'Service Settings',
    keys: [
      'CLIENT_SERVICE',
      'ALLOW_SELF_ORDER_CUSTOMER',
      'ENABLE_RETURN_DESTINATION',
      'ENABLE_AUTO_REALLOCATION',
      'REALLOCATION_INTERVAL_MINUTES',
      'MASKED_PACKAGE_NAME',
    ],
  },
  {
    label: 'Portal Features',
    keys: [
      'PORTAL_ALLOW_INVOICING_SUMMARY',
      'PORTAL_BYPASS_SELF_SERVICE_CLIENT',
      'PORTAL_ALLOW_SCHEDULE',
      'PORTAL_MULTI_STOP_OPTIONS',
      'PORTAL_COD_MODE',
      'PORTAL_CASH_ADVANCE_ENABLED_OUTLETS',
    ],
  },
  {
    label: 'Proof of Delivery',
    keys: [
      'MIN_POP',
      'MAX_POP',
      'MIN_POD',
      'MAX_POD',
      'POD_PER_PACKAGE',
      'ENABLE_RETUR_H0',
      'SHIPPING_DOC_SIGNATURE_OPTIONS',
      'INCLUDE_ACTUAL_RECIPIENT_RELATION',
    ],
  },
  {
    label: 'Radius Configuration',
    keys: [
      'ALLOCATION_RADIUS_INSTANT',
      'ALLOCATION_RADIUS_SAME_DAY',
      'ALLOCATION_RADIUS_FALLBACK',
      'ALLOCATION_RADIUS_MULTI_STOP',
      'DELIVERY_STATUS_UPDATE_GEOFENCE_RADIUS',
    ],
  },
  {
    label: 'Group Order Configuration',
    keys: [
      'MAX_GROUP_DELIVERY_SIZE_INSTANT',
      'MAX_GROUP_DELIVERY_SIZE_SAME_DAY',
      'MAX_GROUP_DELIVERY_SIZE_FALLBACK',
    ],
  },
];

export const getProviderConfig = async (
  providerId: string | number
): Promise<ProviderConfigMap> => {
  try {
    const response = await Axios.get(`/v1/providers/${providerId}/config`);
    return response.data?.data ?? response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const updateProviderConfig = async (
  providerId: string | number,
  data: { key: string; value: any }
): Promise<void> => {
  try {
    await Axios.put(`/v1/providers/${providerId}/config`, data);
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider Users
// ─────────────────────────────────────────────────────────────────────────────

/** A client-portal RBAC role (`m_role`), e.g. `client_admin`. */
export interface ClientRole {
  id: string;
  name: string;
  label: string;
}

export interface ProviderUser {
  id: string | number;
  name: string;
  email: string;
  /** Stored normalized with the country code, e.g. `628123456789`. */
  phoneNumber?: string | null;
  providerOutletId?: string | null;
  providerOutlet?: { id: string | number; name: string } | null;
  providerPitstops?: { id: string | number; name: string; code?: string }[];
  roles?: { id?: string; name: string; label?: string }[];
  createdAt?: string;
  created_at?: string;
}

/**
 * Body of `POST/PATCH /v1/providers/:id/users` on core. The role is sent as
 * `roleId` (an `m_role` uuid from `getClientRoles`) — core rejects a create
 * without it ("roleId" is required) and ignores a role *name*.
 * `providerOutletId: null` clears the outlet scope on update; omitting it
 * leaves it unchanged. Same for `providerPitstopIds` (`[]` clears them).
 */
export interface ProviderUserPayload {
  name?: string;
  email?: string;
  phoneNumber?: string | null;
  password?: string;
  roleId?: string;
  providerOutletId?: string | null;
  providerPitstopIds?: (string | number)[];
}

export interface ProviderUserListResponse {
  data: ProviderUser[];
  pagination?: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export const getProviderUsers = async (
  providerId: string | number,
  params: { page?: number; size?: number; search?: string } = {}
): Promise<ProviderUserListResponse> => {
  try {
    const response = await Axios.get(`/v1/providers/${providerId}/users`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Client-portal roles (`client_super_admin`, `client_admin`, …) — the options
 * for a provider user's role. Back-office-only route on core.
 */
export const getClientRoles = async (): Promise<ClientRole[]> => {
  try {
    const response = await Axios.get('/mgmt/v1/rbac/roles', {
      params: { prefix: 'client' },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const createProviderUser = async (
  providerId: string | number,
  data: ProviderUserPayload
): Promise<ProviderUser> => {
  try {
    const response = await Axios.post(`/v1/providers/${providerId}/users`, data);
    return response.data?.data ?? response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const updateProviderUser = async (
  providerId: string | number,
  userId: string | number,
  data: ProviderUserPayload
): Promise<ProviderUser> => {
  try {
    // Core only routes PATCH here — a PUT 404s.
    const response = await Axios.patch(
      `/v1/providers/${providerId}/users/${userId}`,
      data
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const deleteProviderUser = async (
  providerId: string | number,
  userId: string | number
): Promise<void> => {
  try {
    await Axios.delete(`/v1/providers/${providerId}/users/${userId}`);
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider Sub-Clients
// ─────────────────────────────────────────────────────────────────────────────

export interface ProviderClient {
  id: string;
  providerId: number;
  providerOutletId: string | null;
  referenceCode: string | null;
  label: string | null;
  name: string;
  address: string;
  city?: string;
  zipcode: string | null;
  latitude: number;
  longitude: number;
  phoneNumber: string | null;
  createdAt?: string;
}

export interface ProviderClientListResponse {
  data: ProviderClient[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export const getProviderClients = async (
  providerId: string | number,
  params: { page?: string | number; size?: string | number; name?: string; search?: string } = {}
): Promise<ProviderClientListResponse> => {
  try {
    const response = await Axios.get(`/mgmt/v1/providers/${providerId}/clients`, {
      params: {
        page: params.page ?? 1,
        size: params.size ?? 10,
        name: params.name ?? params.search,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const createProviderClient = async (
  providerId: string | number,
  data: Partial<ProviderClient>
): Promise<ProviderClient> => {
  try {
    const response = await Axios.post(
      `/mgmt/v1/providers/${providerId}/clients`,
      data
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const updateProviderClient = async (
  providerId: string | number,
  clientId: string | number,
  data: Partial<ProviderClient>
): Promise<ProviderClient> => {
  try {
    const response = await Axios.patch(
      `/mgmt/v1/providers/${providerId}/clients/${clientId}`,
      data
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const deleteProviderClient = async (
  providerId: string | number,
  clientId: string | number
): Promise<void> => {
  try {
    await Axios.delete(`/mgmt/v1/providers/${providerId}/clients/${clientId}`);
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};


